import axios from 'axios'

// Create an axios instance with default config.
// No default Content-Type: axios sets application/json for object payloads
// and multipart/form-data (with boundary) for FormData. Forcing json here
// makes axios serialize FormData uploads to JSON, breaking /pdf/upload.
const api = axios.create({
  baseURL: '/api',
})

// Request interceptor for API calls
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export const leaseApi = {
  // Lease Analysis
  analyzeLease: async (file) => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await api.post('/leases/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  // Dashboard
  getDashboardStats: async () => {
    const response = await api.get('/dashboard/stats')
    return response.data
  },

  getRecentAnalyses: async () => {
    const response = await api.get('/leases/recent')
    return response.data
  },

  // Lease Management
  getLeaseById: async (id) => {
    const response = await api.get(`/leases/${id}`)
    return response.data
  },

  updateLeaseMetadata: async (id, metadata) => {
    const response = await api.patch(`/leases/${id}`, metadata)
    return response.data
  },

  deleteLeaseAnalysis: async (id) => {
    const response = await api.delete(`/leases/${id}`)
    return response.data
  },

  // User Profile
  getUserProfile: async () => {
    const response = await api.get('/users/me')
    return response.data
  },

  updateUserProfile: async (data) => {
    const response = await api.patch('/users/me', data)
    return response.data
  },

  // Settings
  getUserSettings: async () => {
    const response = await api.get('/users/settings')
    return response.data
  },

  updateUserSettings: async (settings) => {
    const response = await api.patch('/users/settings', settings)
    return response.data
  },
}

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle specific error cases
    if (error.response) {
      // Paywall: out of credits anywhere -> legal gate + checkout (unless
      // already there). Terms re-acceptance is a harmless server re-stamp.
      if (
        error.response.status === 402 &&
        !window.location.pathname.startsWith('/legal')
      ) {
        window.location.assign('/legal?plan=standard')
      }
      // Expired/invalid token: clear it and bounce to login. RequireAuth only
      // checks that a token is PRESENT, not that it's still valid, so a stale
      // token (JWT expires after 24h) would otherwise strand the user on a
      // guarded page with every call 401ing. Guarded so a failed login POST
      // (401 on /login) doesn't loop. Mirrors the 402 -> /legal guard.
      if (
        error.response.status === 401 &&
        !window.location.pathname.startsWith('/login')
      ) {
        localStorage.removeItem('auth_token')
        window.location.assign('/login')
      }
      switch (error.response.status) {
        case 400:
          error.message = 'Invalid request'
          break
        case 401:
          error.message = 'Unauthorized access'
          break
        case 402:
          error.message = error.response.data?.detail || 'Payment required'
          break
        case 403:
          error.message = 'Forbidden access'
          break
        case 404:
          error.message = 'Resource not found'
          break
        case 422: {
          const detail = error.response.data?.detail
          if (typeof detail === 'string') {
            error.message = detail
          } else if (Array.isArray(detail) && detail.length > 0) {
            error.message = detail.map((d) => d.msg).filter(Boolean).join(', ') || 'Validation error'
          } else {
            error.message = 'Validation error'
          }
          break
        }
        case 500:
          error.message = 'Server error'
          break
        default:
          error.message = 'An unexpected error occurred'
      }
    } else if (error.request) {
      error.message = 'No response received from server'
    } else {
      error.message = 'Error setting up request'
    }
    return Promise.reject(error)
  }
)

export default api
